import React, { useEffect, useState } from "react";

import PhoneInput from "react-phone-input-2";

import "react-phone-input-2/lib/style.css";

import "./BootcampPayment.css";

// =====================================================
// BOOTCAMP CONFIG
// =====================================================

const SERVICE_NAME =
  "Forge Your Peak - Bootcamp";

const BOOTCAMP_PRICE = 499;

const BOOTCAMP_AMOUNT = 49900;


// =====================================================
// LOAD RAZORPAY SCRIPT
// =====================================================

const loadRazorpayScript = () => {
  return new Promise((resolve) => {

    // -----------------------------------------------
    // ALREADY LOADED
    // -----------------------------------------------

    if (window.Razorpay) {
      resolve(true);
      return;
    }

    // -----------------------------------------------
    // CHECK EXISTING SCRIPT
    // -----------------------------------------------

    const existingScript =
      document.querySelector(
        'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
      );

    if (existingScript) {

      existingScript.addEventListener(
        "load",
        () => resolve(true)
      );

      existingScript.addEventListener(
        "error",
        () => resolve(false)
      );

      return;
    }

    // -----------------------------------------------
    // CREATE SCRIPT
    // -----------------------------------------------

    const script =
      document.createElement("script");

    script.src =
      "https://checkout.razorpay.com/v1/checkout.js";

    script.async = true;

    script.onload = () => {
      resolve(true);
    };

    script.onerror = () => {
      resolve(false);
    };

    document.body.appendChild(script);
  });
};


// =====================================================
// COMPONENT
// =====================================================

const BootcampPayment = () => {

  // ===================================================
  // FORM STATE
  // ===================================================

  const [
    formData,
    setFormData,
  ] = useState({
    name: "",
    email: "",
    phone: "",
  });


  // ===================================================
  // PAYMENT STATE
  // ===================================================

  const [
    isLoading,
    setIsLoading,
  ] = useState(false);


  // ===================================================
  // STATUS STATE
  // ===================================================

  const [
    statusMessage,
    setStatusMessage,
  ] = useState("");

  const [
    statusType,
    setStatusType,
  ] = useState("");


  // ===================================================
  // HIDE NAVBAR / FOOTER
  // ===================================================

  useEffect(() => {

    window.scrollTo(0, 0);

    const navbar =
      document.querySelector(
        "header, .navbar"
      );

    const footer =
      document.querySelector("footer");

    if (navbar) {
      navbar.style.display = "none";
    }

    if (footer) {
      footer.style.display = "none";
    }

    return () => {

      if (navbar) {
        navbar.style.display = "";
      }

      if (footer) {
        footer.style.display = "";
      }

    };

  }, []);


  // ===================================================
  // INPUT CHANGE
  // ===================================================

  const handleChange = (e) => {

    const {
      name,
      value,
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setStatusMessage("");
    setStatusType("");

  };


  // ===================================================
  // PHONE CHANGE
  // ===================================================

  const handlePhoneChange = (phone) => {

    setFormData((prev) => ({
      ...prev,
      phone,
    }));

    setStatusMessage("");
    setStatusType("");

  };


  // ===================================================
  // VALIDATE CUSTOMER DETAILS
  // ===================================================

  const validateCustomerDetails = () => {

    const name =
      formData.name.trim();

    const email =
      formData.email
        .trim()
        .toLowerCase();

    const phone =
      formData.phone.trim();


    // -----------------------------------------------
    // NAME
    // -----------------------------------------------

    if (!name) {
      return "Please enter your full name.";
    }

    if (name.length < 2) {
      return "Please enter a valid name.";
    }


    // -----------------------------------------------
    // EMAIL
    // -----------------------------------------------

    if (!email) {
      return "Please enter your email address.";
    }

    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      return "Please enter a valid email address.";
    }


    // -----------------------------------------------
    // PHONE
    // -----------------------------------------------

    if (!phone) {
      return "Please enter your phone number.";
    }

    const digits =
      phone.replace(/\D/g, "");

    if (digits.length < 10) {
      return "Please enter a valid phone number.";
    }


    return null;

  };


  // ===================================================
  // SHOW ERROR
  // ===================================================

  const showError = (message) => {

    setStatusMessage(
      message ||
        "Something went wrong. Please try again."
    );

    setStatusType("error");

  };


  // ===================================================
  // SHOW SUCCESS
  // ===================================================

  const showSuccess = (message) => {

    setStatusMessage(message);

    setStatusType("success");

  };


  // ===================================================
  // PAYMENT SUCCESS
  // ===================================================

  const handlePaymentSuccess = (
    verificationData
  ) => {

    console.log(
      "✅ Bootcamp payment verified:",
      verificationData
    );


    showSuccess(
      "Payment successful! Your Bootcamp access is being activated."
    );


    /*
      IMPORTANT:

      User activation / DB save is handled
      by Razorpay webhook on backend.

      Frontend does NOT directly activate
      or create the user.
    */

  };


  // ===================================================
  // OPEN RAZORPAY CHECKOUT
  // ===================================================

  const openRazorpayCheckout =
    async (orderData) => {

      // -----------------------------------------------
      // RAZORPAY CHECK
      // -----------------------------------------------

      if (!window.Razorpay) {

        showError(
          "Razorpay could not be loaded. Please refresh and try again."
        );

        setIsLoading(false);

        return;

      }


      // -----------------------------------------------
      // CUSTOMER DATA
      // -----------------------------------------------

      const customerName =
        formData.name.trim();

      const customerEmail =
        formData.email
          .trim()
          .toLowerCase();

      const customerPhone =
        formData.phone.trim();


      // -----------------------------------------------
      // SERVER AMOUNT
      // -----------------------------------------------

      const backendAmount =
        Number(
          orderData.order.amount
        );


      // -----------------------------------------------
      // SAFETY CHECK
      // -----------------------------------------------

      if (
        !Number.isInteger(
          backendAmount
        ) ||
        backendAmount !==
          BOOTCAMP_AMOUNT
      ) {

        showError(
          "Invalid Bootcamp payment amount received from server."
        );

        setIsLoading(false);

        return;

      }


      // =================================================
      // RAZORPAY OPTIONS
      // =================================================

      const options = {

        key:
          orderData.keyId,

        amount:
          backendAmount,

        currency:
          orderData.order.currency ||
          "INR",

        name:
          "Forge Your Peak",

        description:
          SERVICE_NAME,

        order_id:
          orderData.order.id,


        // ---------------------------------------------
        // CHECKOUT CONFIG
        // ---------------------------------------------

        config: {

          display: {

            hide: [
              {
                method: "emi",
              },
            ],

            preferences: {
              show_default_blocks: true,
            },

          },

        },


        // ---------------------------------------------
        // PREFILL
        // ---------------------------------------------

        prefill: {

          name:
            customerName,

          email:
            customerEmail,

          contact:
            customerPhone,

        },


        // ---------------------------------------------
        // NOTES
        // ---------------------------------------------

        notes: {

          service:
            SERVICE_NAME,

          offer:
            "normal",

        },


        // ---------------------------------------------
        // THEME
        // ---------------------------------------------

        theme: {

          color:
            "#242951",

        },


        // =================================================
        // PAYMENT SUCCESS
        // =================================================

        handler:
          async function (
            response
          ) {

            console.log(
              "✅ Razorpay payment response:",
              response
            );


            try {

              // -----------------------------------------
              // SHOW VERIFYING
              // -----------------------------------------

              setStatusMessage(
                "Verifying your payment..."
              );

              setStatusType("success");


              // =========================================
              // VERIFY PAYMENT
              // =========================================

              const verifyResponse =
                await fetch(
                  "/api/razorpay/verify-payment",
                  {
                    method: "POST",

                    headers: {
                      "Content-Type":
                        "application/json",
                    },

                    body:
                      JSON.stringify({
                        razorpay_order_id:
                          response
                            .razorpay_order_id,

                        razorpay_payment_id:
                          response
                            .razorpay_payment_id,

                        razorpay_signature:
                          response
                            .razorpay_signature,
                      }),
                  }
                );


              // =========================================
              // READ RESPONSE
              // =========================================

              let verifyData = null;

              try {

                verifyData =
                  await verifyResponse.json();

              } catch {

                verifyData = null;

              }


              // =========================================
              // VERIFICATION FAILED
              // =========================================

              if (
                !verifyResponse.ok ||
                !verifyData?.success
              ) {

                console.error(
                  "❌ Payment verification failed:",
                  verifyData
                );


                showError(
                  verifyData?.message ||
                    "Payment verification failed. Please contact support."
                );


                setIsLoading(false);

                return;

              }


              // =========================================
              // VERIFIED
              // =========================================

              console.log(
                "✅ Payment signature verified successfully."
              );


              // =========================================
              // META PIXEL PURCHASE
              // =========================================

              if (window.fbq) {

                window.fbq(
                  "trackSingle",

                  "2060088607979588",

                  "Purchase",

                  {
                    content_category:
                      "Product Purchase",

                    content_name:
                      SERVICE_NAME,

                    value:
                      BOOTCAMP_PRICE,

                    currency:
                      "INR",

                    contents: [
                      {
                        id:
                          "fyp-bootcamp",

                        quantity: 1,
                      },
                    ],

                    content_type:
                      "product",
                  },

                  {
                    eventID:
                      response
                        .razorpay_payment_id,
                  }
                );


                console.log(
                  "✅ Meta Bootcamp Purchase event tracked."
                );

              }


              // =========================================
              // SUCCESS
              // =========================================

              handlePaymentSuccess(
                verifyData
              );


              // =========================================
              // OPTIONAL REDIRECT
              // =========================================

              /*
                If you want redirect after payment,
                uncomment this.

                setTimeout(() => {
                  window.location.href =
                    "/dashboard-login";
                }, 1500);
              */

            } catch (error) {

              console.error(
                "❌ Payment verification request error:",
                error
              );


              showError(
                "Unable to verify your payment. Please contact support if the amount was deducted."
              );

            } finally {

              setIsLoading(false);

            }

          },


        // =================================================
        // CHECKOUT CLOSED
        // =================================================

        modal: {

          ondismiss:
            function () {

              console.log(
                "ℹ️ Razorpay checkout closed."
              );


              setIsLoading(false);


              setStatusMessage(
                "Payment was cancelled."
              );


              setStatusType("error");

            },

        },

      };


      // =================================================
      // CREATE RAZORPAY INSTANCE
      // =================================================

      const razorpay =
        new window.Razorpay(
          options
        );


      // =================================================
      // PAYMENT FAILED
      // =================================================

      razorpay.on(
        "payment.failed",
        function (response) {

          console.error(
            "❌ Razorpay payment failed:",
            response?.error
          );


          const errorDescription =
            response?.error
              ?.description;


          showError(
            errorDescription ||
              "Payment failed. Please try again."
          );


          setIsLoading(false);

        }
      );


      // =================================================
      // OPEN CHECKOUT
      // =================================================

      razorpay.open();

    };


  // ===================================================
  // CREATE ORDER + PAYMENT
  // ===================================================

  const handlePayment =
    async () => {

      // -----------------------------------------------
      // CLEAR STATUS
      // -----------------------------------------------

      setStatusMessage("");
      setStatusType("");


      // -----------------------------------------------
      // VALIDATE
      // -----------------------------------------------

      const validationError =
        validateCustomerDetails();


      if (validationError) {

        showError(
          validationError
        );

        return;

      }


      // -----------------------------------------------
      // PREVENT DOUBLE CLICK
      // -----------------------------------------------

      if (isLoading) {
        return;
      }


      setIsLoading(true);


      try {

        // =============================================
        // LOAD RAZORPAY
        // =============================================

        const razorpayLoaded =
          await loadRazorpayScript();


        if (!razorpayLoaded) {

          throw new Error(
            "Unable to load Razorpay."
          );

        }


        // =============================================
        // CLEAN CUSTOMER DATA
        // =============================================

        const cleanName =
          formData.name.trim();

        const cleanEmail =
          formData.email
            .trim()
            .toLowerCase();

        const cleanPhone =
          formData.phone.trim();


        // =============================================
        // CREATE BACKEND ORDER
        // =============================================

        console.log(
          "🔄 Creating Bootcamp Razorpay order..."
        );


        const orderResponse =
          await fetch(
            "/api/razorpay/create-order",
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",
              },

              body:
                JSON.stringify({

                  name:
                    cleanName,

                  email:
                    cleanEmail,

                  phone:
                    cleanPhone,

                  service:
                    SERVICE_NAME,

                  // ---------------------------------
                  // BOOTCAMP HAS NO OFFER
                  // ---------------------------------

                  offer:
                    "normal",

                  offerToken:
                    null,

                  paymentPlan:
                    "ONE_TIME",

                }),

            }
          );


        // =============================================
        // READ ORDER RESPONSE
        // =============================================

        let orderData = null;


        try {

          orderData =
            await orderResponse.json();

        } catch {

          orderData = null;

        }


        // =============================================
        // CREATE ORDER FAILED
        // =============================================

        if (
          !orderResponse.ok ||
          !orderData?.success
        ) {

          console.error(
            "❌ Create order failed:",
            orderData
          );


          throw new Error(
            orderData?.message ||
              "Unable to create payment order."
          );

        }


        // =============================================
        // ORDER VALIDATION
        // =============================================

        if (
          !orderData.keyId ||
          !orderData.order?.id
        ) {

          console.error(
            "❌ Invalid order response:",
            orderData
          );


          throw new Error(
            "Invalid payment order received from server."
          );

        }


        // =============================================
        // SERVER AMOUNT CHECK
        // =============================================

        if (
          Number(
            orderData.order.amount
          ) !==
          BOOTCAMP_AMOUNT
        ) {

          console.error(
            "❌ Unexpected Bootcamp amount:",
            orderData.order.amount
          );


          throw new Error(
            "Invalid Bootcamp payment amount."
          );

        }


        // =============================================
        // SERVICE CHECK
        // =============================================

        if (
          orderData.service &&
          orderData.service !==
            SERVICE_NAME
        ) {

          console.error(
            "❌ Unexpected service:",
            orderData.service
          );


          throw new Error(
            "Invalid Bootcamp service."
          );

        }


        console.log(
          "✅ Bootcamp Razorpay order created:",
          {
            orderId:
              orderData.order.id,

            amount:
              orderData.order.amount,

            currency:
              orderData.order.currency,

            service:
              orderData.service,
          }
        );


        // =============================================
        // OPEN RAZORPAY
        // =============================================

        await openRazorpayCheckout(
          orderData
        );

      } catch (error) {

        console.error(
          "❌ Bootcamp payment error:",
          error
        );


        showError(
          error.message ||
            "Unable to start payment. Please try again."
        );


        setIsLoading(false);

      }

    };


  // ===================================================
  // FORMAT PRICE
  // ===================================================

  const formatINR = (amount) => {

    return Number(
      amount
    ).toLocaleString(
      "en-IN"
    );

  };


  // ===================================================
  // RENDER
  // ===================================================

  return (

    <div className="bootcamp-payment-page">

      {/* =================================================
          LEFT SIDE
      ================================================= */}

      <section className="bootcamp-payment-left">

        <div className="bootcamp-left-inner">

          {/* =================================================
              LOGO
          ================================================= */}

          <div className="bootcamp-company-logo">

            <img
              src="/logo.png"
              alt="Forge Your Peak"
            />

          </div>


          {/* =================================================
              PRODUCT HEADER
          ================================================= */}

          <div className="bootcamp-product-header">

            <h1>
              Forge Your Peak – Bootcamp
            </h1>

            <p>
              By ForgeYourPeak
            </p>


            {/* PRICE */}

            <div className="bootcamp-product-price">

              ₹
              {formatINR(
                BOOTCAMP_PRICE
              )}

            </div>

          </div>


          {/* =================================================
              HERO IMAGE
          ================================================= */}

          <div className="bootcamp-image-wrapper">

            <img
              src="/FYP-Bootcamp/bootcamp.jpg"
              alt="Forge Your Peak Bootcamp"
              className="bootcamp-main-image"
            />

          </div>


          {/* =================================================
              PRODUCT DESCRIPTION
          ================================================= */}

          <div className="bootcamp-product-description">

            <p className="bootcamp-bold-intro">

              Your reactions feel late.
              <br />

              Your tracking feels inconsistent.
              <br />

              Your vision feels “off” under speed.

            </p>


            <p className="bootcamp-quote">

              "Discover What's Actually Slowing You Down"

            </p>


            <p>
              Your reactions feel late.
            </p>


            <p>

              Your tracking feels inconsistent.
              But here's what you don't know:
              elite athletes react under 200ms.
              Where do you stand right now?

            </p>


            <p>

              The Eye Health Reset – Athlete Edition
              is a 3-day neuro-visual bootcamp that
              starts with your baseline. Measure your
              vision and reaction speed. Learn two
              foundational drills that sharpen both.
              Feel clearer focus and faster pickup
              within days.

            </p>


            <p>

              No equipment. 10-15 minutes daily.
              Immediate, noticeable shifts.

            </p>


            <p>

              By Day 3, most athletes report:
              Things look clearer. I react earlier.
              My tracking feels steadier.

            </p>


            <p>

              Test your baseline. Train your edge.
              Join the reset now.

            </p>


            <p className="bootcamp-final-text">

              Join the 3-Day Eye Health Reset –
              ₹499

            </p>

          </div>

        </div>

      </section>


      {/* =================================================
          RIGHT SIDE
      ================================================= */}

      <section className="bootcamp-payment-right">

        <div className="bootcamp-payment-right-inner">

          {/* =================================================
              PAYMENT DETAILS
          ================================================= */}

          <h2>
            Payment details
          </h2>


          <p className="bootcamp-payment-subtitle">

            Enter your details to continue securely.

          </p>


          {/* =================================================
              BILLING CARD
          ================================================= */}

          <div className="bootcamp-billing-card">

            <h3>
              Billing information
            </h3>


            <div className="bootcamp-billing-fields">

              {/* NAME */}

              <input
                type="text"
                name="name"
                placeholder="Name"
                value={
                  formData.name
                }
                onChange={
                  handleChange
                }
                disabled={
                  isLoading
                }
                autoComplete="name"
              />


              {/* EMAIL */}

              <input
                type="email"
                name="email"
                placeholder="Email"
                value={
                  formData.email
                }
                onChange={
                  handleChange
                }
                disabled={
                  isLoading
                }
                autoComplete="email"
              />


              {/* PHONE */}

              <div className="bootcamp-phone-wrapper">

                <PhoneInput
                  country="in"

                  value={
                    formData.phone
                  }

                  onChange={
                    handlePhoneChange
                  }

                  disabled={
                    isLoading
                  }

                  enableSearch

                  countryCodeEditable={
                    false
                  }

                  containerClass={
                    "bootcamp-phone-container"
                  }

                  inputClass={
                    "bootcamp-phone-input"
                  }

                  buttonClass={
                    "bootcamp-phone-button"
                  }

                  dropdownClass={
                    "bootcamp-phone-dropdown"
                  }

                  inputProps={{
                    name:
                      "phone",

                    required:
                      true,

                    autoComplete:
                      "tel",
                  }}

                />

              </div>

            </div>

          </div>


          {/* =================================================
              ORDER SUMMARY
          ================================================= */}

          <div className="bootcamp-order-card">

            <div className="bootcamp-order-top-line" />


            <div className="bootcamp-order-content">

              <h3>
                Order summary
              </h3>


              <div className="bootcamp-service-row">

                <span>
                  {SERVICE_NAME}
                </span>


                <strong>

                  ₹
                  {formatINR(
                    BOOTCAMP_PRICE
                  )}

                </strong>

              </div>


              <div className="bootcamp-divider" />


              <div className="bootcamp-total-row">

                <span>
                  Amount to be paid
                </span>


                <strong>

                  ₹
                  {formatINR(
                    BOOTCAMP_PRICE
                  )}

                </strong>

              </div>

            </div>

          </div>


          {/* =================================================
              STATUS MESSAGE
          ================================================= */}

          {statusMessage && (

            <div
              className={`bootcamp-status-message ${
                statusType === "error"
                  ? "error"
                  : "success"
              }`}
            >

              {statusMessage}

            </div>

          )}


          {/* =================================================
              PAYMENT BUTTON
          ================================================= */}

          <button
            type="button"
            className="bootcamp-pay-button"
            onClick={
              handlePayment
            }
            disabled={
              isLoading
            }
          >

            {isLoading
              ? "Processing..."
              : `Proceed to Pay ₹${formatINR(
                  BOOTCAMP_PRICE
                )}`}

          </button>


          {/* =================================================
              PAYMENT METHODS
          ================================================= */}

          <div className="bootcamp-payment-methods">

            <span>
              Secure payment powered by Razorpay
            </span>

          </div>

        </div>

      </section>

    </div>

  );

};


export default BootcampPayment;